import { useState, useEffect } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import {
  Box, Chip, Pagination, Paper, Stack, Table, TableBody, Typography,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Button, IconButton
} from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from "prop-types";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FormControl, Select, MenuItem, InputLabel, Switch } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { getEmpresas } from "../../actions/empresas";
import { isEmpty } from "../../components/common/helpers";
import { getServicioArrayId } from "../../actions/servicios";
import InvoiceUploadModal from "../modals/InvoiceUploadModal";
import RequestInvoiceDetailModal from "../modals/RequestInvoiceDetailModal";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";

dayjs.extend(isBetween);

const getBadge = (statusType) => {
  switch (statusType) {
    case 1:
      return <Chip className="badge_status" color="success" size="small" label="Completado" />;
    case 0:
      return <Chip className="badge_status" color="warning" size="small" label="En Proceso" />;
    case 2:
      return <Chip className="badge_status" color="error" size="small" label="Fallido" />;
  }
};

function descendingComparator(a, b, orderBy) {
  // Las filas sin la fecha seleccionada (ej. operaciones sin facturar) van al final
  const aVal = a[orderBy] ? dayjs(a[orderBy]).valueOf() : 0;
  const bVal = b[orderBy] ? dayjs(b[orderBy]).valueOf() : 0;
  if (bVal < aVal) {
    return -1;
  }
  if (bVal > aVal) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy = "date") {
  return order === "asc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const TableInv = ({ history, moment, onSelect }) => {
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [operaciones, setOperaciones] = useState([]);
  const [invoiceItem, setInvoiceItem] = useState(null);
  const [requestDetailItem, setRequestDetailItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);

  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // Campo de fecha que gobierna filtro/orden/columna: "date" (registro) o "fecha_facturacion"
  const [dateField, setDateField] = useState("date");

  const dispatch = useDispatch();
  const empresas = useSelector(state => state.empresas.empresas);

  const totalPages = Math.ceil(history.length / rowsPerPage);

  useEffect(() => {
    if (isEmpty(empresas)) {
      dispatch(getEmpresas());
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(history)) {
      getNameS()
    }
  }, [history]);

  const getNameS = async () => {
    const res = await getServicioArrayId(history);
    return setOperaciones(res)
  }


  const handleChangePage = newPage => {
    setPage(newPage);
  };

  const createSortHandler = event => {
    const isAsc = order === "asc";
    setOrder(isAsc ? "desc" : "asc");
  };

  const handleDateFilterChange = (event) => {
    const value = event.target.value;
    setDateFilter(value);
    const now = dayjs();

    if (value === "this-month") {
      setStartDate(now.startOf('month'));
      setEndDate(now.endOf('month'));
    } else if (value === "last-month") {
      setStartDate(now.subtract(1, 'month').startOf('month'));
      setEndDate(now.subtract(1, 'month').endOf('month'));
    } else if (value === "last-7-days") {
      setStartDate(now.subtract(7, 'days'));
      setEndDate(now);
    } else if (value === "all") {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const filteredHistory = history.filter(item => {
    if (!startDate || !endDate) return true;
    const itemDate = dayjs(item.date);
    return itemDate.isBetween(startDate, endDate, 'day', '[]');
  });

  const sortedAndPaginatedOperaciones = operaciones
    .filter(op => {
      if (!startDate || !endDate) return true;
      const value = op[dateField];
      if (!value) return false; // sin fecha en el campo elegido → fuera del rango
      const opDate = dayjs(value);
      return opDate.isBetween(startDate, endDate, 'day', '[]');
    })
    .sort(getComparator(order, dateField));

  const handleDownload = async (file) => {
    try {
      if (Array.isArray(file) && file.length > 1) {
        // Lógica para descargar ZIP si hay múltiples archivos
        const zip = new JSZip();
        const promises = file.map(async (url, index) => {
          const response = await fetch(`${url}?t=${new Date().getTime()}`, { cache: "reload" });
          const blob = await response.blob();
          // Obtener extensión o usar default
          let ext = 'jpg';
          const fileNameFromUrl = url.split('/').pop().split('?')[0].toLowerCase();

          if (blob.type === 'application/pdf') ext = 'pdf';
          else if (blob.type === 'image/png') ext = 'png';
          else if (blob.type === 'image/jpeg') ext = 'jpg';
          else if (blob.type === 'text/xml' || blob.type === 'application/xml' || fileNameFromUrl.endsWith('_xml')) ext = 'xml';
          else {
            const parts = fileNameFromUrl.split('.');
            if (parts.length > 1) {
              ext = parts.pop();
            }
          }
          zip.file(`comprobante_${index + 1}.${ext}`, blob);
        });

        await Promise.all(promises);

        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, "documentos.zip");
      } else {
        // Lógica original para un solo archivo (string o array de 1 elemento)
        const fileUrl = Array.isArray(file) ? file[0] : file;

        // Agregar un parámetro único para evitar la caché
        const uniqueUrl = `${fileUrl}?t=${new Date().getTime()}`;

        const response = await fetch(uniqueUrl, { cache: "reload" }); // Evitar caché
        const blob = await response.blob();

        // Extraer nombre original de la URL
        let fileName = fileUrl.split('/').pop().split('?')[0];
        console.log("es xml", fileName, blob.type)

        // Asegurar extensión XML si es necesario (mejorado con check de sufijo _xml)
        if (blob.type === 'text/xml' || blob.type === 'application/octet-stream' || fileName.toLowerCase().endsWith('_xml')) {
          if (!fileName.toLowerCase().endsWith('.xml')) {
            // Reemplazar _xml por .xml si existe al final, o simplemente añadirlo
            if (fileName.toLowerCase().endsWith('_xml')) {
              fileName = fileName.substring(0, fileName.length - 4) + '.xml';
            } else {
              fileName += '.xml';
            }
          }
        } else if (blob.type === 'application/pdf' && !fileName.toLowerCase().endsWith('.pdf')) {
          if (fileName.toLowerCase().endsWith('_pdf')) {
            fileName = fileName.substring(0, fileName.length - 4) + '.pdf';
          } else {
            fileName += '.pdf';
          }
        }

        // Crear un objeto URL para la descarga
        const newObjectURL = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = newObjectURL;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Liberar memoria
        URL.revokeObjectURL(newObjectURL);
      }
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
    }
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              color={dateField === "date" ? "primary" : "text.secondary"}
            >
              Registro
            </Typography>
            <Switch
              checked={dateField === "fecha_facturacion"}
              onChange={(e) => {
                setDateField(e.target.checked ? "fecha_facturacion" : "date");
                setPage(1);
              }}
            />
            <Typography
              variant="body2"
              color={dateField === "fecha_facturacion" ? "primary" : "text.secondary"}
            >
              Facturación
            </Typography>
          </Stack>

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="date-filter-label">Fecha</InputLabel>
            <Select
              labelId="date-filter-label"
              value={dateFilter}
              label="Fecha"
              onChange={handleDateFilterChange}
            >
              <MenuItem value="all">Todo</MenuItem>
              <MenuItem value="this-month">Este mes</MenuItem>
              <MenuItem value="last-month">Mes pasado</MenuItem>
              <MenuItem value="last-7-days">Últimos 7 días</MenuItem>
              <MenuItem value="custom">Personalizado</MenuItem>
            </Select>
          </FormControl>

          {dateFilter === 'custom' && (
            <>
              <DatePicker
                label="Fecha inicio"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="Fecha fin"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </>
          )}
        </Stack>
      </LocalizationProvider>

      <TableContainer
        className="table-content"
        components={{
          Container: props => <Paper {...props} elevation={0} />
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={true}
                  direction={order}
                  onClick={createSortHandler}
                >
                  {dateField === "fecha_facturacion" ? "Fecha de facturación" : "Fecha de registro"}
                  <Box mt={2} component="span">
                    {order === "desc"
                      ? <KeyboardArrowDownIcon />
                      : <KeyboardArrowUpIcon />
                    }
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell>Tipo de operación</TableCell>
              <TableCell>Monto</TableCell>
              <TableCell>Servicio</TableCell>
              <TableCell>Comprobante</TableCell>
              <TableCell>Estatus</TableCell>
              <TableCell>Factura</TableCell>
              <TableCell>Empresa</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              (
                rowsPerPage > 0 ?
                  sortedAndPaginatedOperaciones.slice((page - 1) * rowsPerPage, page * rowsPerPage) :
                  sortedAndPaginatedOperaciones
              ).map((row, i) => {
                const formatoMonto = row.monto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                return (
                  <TableRow
                    key={i}
                    sx={{ "& td, & th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row[dateField] ? moment(row[dateField]).format("YYYY / MM / DD") : "—"}
                    </TableCell>
                    <TableCell>
                      {
                        isEmpty(row.nombreServicio) ? (
                          <center>
                            {row.type}
                          </center>
                        ) : (
                          <center>
                            Servicio
                          </center>
                        )
                      }
                    </TableCell>
                    <TableCell>
                      <center>
                        $ {formatoMonto}
                      </center>
                    </TableCell>
                    <TableCell>
                      {
                        (isEmpty(row.nombreServicio) && isEmpty(row.servicioEmpresa)) ?
                          "Sin servicio" : isEmpty(row.nombreServicio) ?
                            row.servicioEmpresa :
                            row.nombreServicio
                      }
                    </TableCell>
                    <TableCell>
                      {
                        row.metodo === "Transferencia" ? (
                          <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
                            <Button onClick={() => onSelect(row)}>
                              Ver
                            </Button>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDownload(row.file)}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : row.metodo === "paypal" ? (
                          <center>
                            <Typography color="primary">
                              Paypal
                            </Typography>
                          </center>
                        ) : row.metodo === "mercadopago" ? (
                          <center>
                            <Typography color="primary">
                              Mercado Pago
                            </Typography>
                          </center>
                        ) : row.metodo === 'googlePay' ? (
                          <center>
                            <Typography color="primary">
                              Google Pay
                            </Typography>
                          </center>
                        ) : row.metodo === "solicitud" ? (
                          <Stack direction="row" spacing={0} justifyContent="center" alignItems="center">
                            <Button 
                              onClick={() => {
                                if (!isEmpty(row.extraData?.comprobante)) {
                                  onSelect(row);
                                } else {
                                  setRequestDetailItem(row.extraData || row);
                                }
                              }}
                            >
                              Ver
                            </Button>
                          </Stack>
                        ) : (
                          <center>
                            <Typography color="primary">
                              Apple Pay
                            </Typography>
                          </center>
                        )
                      }
                    </TableCell>
                    <TableCell>
                      {getBadge(row.status)}
                    </TableCell>
                    <TableCell>
                      <center>
                        {!isEmpty(row.factura_pdf) || !isEmpty(row.factura_xml) ? (
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {!isEmpty(row.factura_pdf) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleDownload(row.factura_pdf)}
                              >
                                PDF
                              </Button>
                            )}
                            {!isEmpty(row.factura_xml) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleDownload(row.factura_xml)}
                              >
                                XML
                              </Button>
                            )}
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => setInvoiceItem(row)}
                              disabled={row.status !== 1 && row.metodo !== 'solicitud'}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteItem(row)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ) : (
                          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => setInvoiceItem(row)}
                              disabled={row.status !== 1 && row.metodo !== 'solicitud'}
                            >
                              Factura
                            </Button>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteItem(row)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                      </center>
                    </TableCell>
                    <TableCell>
                      <center>
                        <Typography color="primary">
                          {
                            row.metodo === "solicitud" ? (
                              row.extraData?.razon_social || 'No aplica'
                            ) : row.empresa && !isEmpty(empresas) ? (
                              empresas.find(e => e._id === row.empresa)?.razon || 'No aplica'
                            ) : row.nombreEmpresa || 'No aplica'
                          }
                        </Typography>
                      </center>
                    </TableCell>
                  </TableRow>
                )
              }
              )
            }
          </TableBody>
        </Table>
      </TableContainer>
      <Stack className="pagination" spacing={2} mt={3}>
        <Pagination
          sx={{ width: "100%" }}
          color="primary"
          shape="rounded"
          count={Math.ceil(sortedAndPaginatedOperaciones.length / rowsPerPage)}
          onChange={(event, value) => handleChangePage(value)}
          page={page}
          size="large"
        />
      </Stack>
      <InvoiceUploadModal
        open={!!invoiceItem}
        handleClose={() => setInvoiceItem(null)}
        item={invoiceItem}
      />
      <RequestInvoiceDetailModal
        open={!!requestDetailItem}
        handleClose={() => setRequestDetailItem(null)}
        data={requestDetailItem}
      />
      <DeleteConfirmationModal
        open={!!deleteItem}
        handleClose={() => setDeleteItem(null)}
        item={deleteItem}
      />
    </>
  )
}

TableInv.propTypes = {
  history: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
}

export default TableInv;